"use client";

import { useForm, useWatch } from "react-hook-form";
import {
  HiSearch,
  HiExternalLink,
  HiClipboardCopy,
  HiFilter,
  HiQuestionMarkCircle,
} from "@/components/icons";
import { FaReddit } from "@/components/icons";
import Link from "next/link";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

import { useToast } from "@/hooks/use-toast";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import { redditAdvancedSearchFAQ } from "@/lib/data/faq/reddit-advanced-search";

type RedditSearchFormValues = {
  // General
  query: string;
  subreddit: string;
  author: string;
  site: string; // url domain

  // Boolean
  titleOnly: boolean;
  selfText: boolean; // self:yes
  nsfw: "include" | "exclude" | "only"; // nsfw:yes/no

  // Sorting & Time
  sort: "relevance" | "hot" | "top" | "new" | "comments";
  time: "all" | "year" | "month" | "week" | "day" | "hour";
};

export default function RedditAdvancedSearchClient() {
  const { register, control } = useForm<RedditSearchFormValues>({
    defaultValues: {
      nsfw: "exclude",
      sort: "relevance",
      time: "all",
    },
  });

  const values = useWatch({ control });
  const { toast } = useToast();

  const queryPreview = (() => {
    const queryParts: string[] = [];

    if (values.query) queryParts.push(values.query);
    if (values.subreddit) queryParts.push(`subreddit:${values.subreddit.replace("r/", "")}`);
    if (values.author) queryParts.push(`author:${values.author.replace("u/", "")}`);
    if (values.site) queryParts.push(`site:${values.site}`);
    if (values.titleOnly) queryParts.push(`title:${values.query || "*"}`);

    if (values.selfText) queryParts.push(`self:yes`);

    if (values.nsfw === "exclude") queryParts.push(`nsfw:no`);
    else if (values.nsfw === "only") queryParts.push(`nsfw:yes`);

    return queryParts.join(" ");
  })();

  const fullUrl = (() => {
    const baseUrl = "https://www.reddit.com/search";
    const params = new URLSearchParams();

    params.set("q", queryPreview);

    if (values.sort) params.set("sort", values.sort);
    if (values.time) params.set("t", values.time);

    return `${baseUrl}?${params.toString()}`;
  })();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(queryPreview);
    toast({
      title: "Copied to clipboard",
      description: "Search query copied to clipboard.",
    });
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Reddit Search Tool"
        description="Uncover niche discussions and hidden communities on Reddit. Search by author, subreddit, site domain, title, self-posts, NSFW filtering, and more."
        url="https://plugmonkey.xyz/tool/reddit-advanced-search/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Reddit Search Tool",
            url: "https://plugmonkey.xyz/tool/reddit-advanced-search/",
          },
        ]}
        faq={redditAdvancedSearchFAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-orange-500/10 rounded-full text-orange-500">
          <FaReddit className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Reddit Search Tool</h1>
          <p className="text-muted-foreground mt-1">
            Build advanced Reddit search queries without memorizing operators.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Reddit's search can be overwhelming, but using advanced filters requires knowing the right
          operators and syntax. This tool makes it simple—build complex searches with filters for
          subreddits, authors, domains, post types, NSFW content, and more, all through a clean
          interface.
        </p>
        <p>
          Perfect for researchers, marketers, and anyone who needs to find specific discussions on
          Reddit. Search within niche communities, find posts by specific users, filter by content
          type (self-posts vs. links), or narrow down to exactly what you're looking for. Generate
          your search query, copy it, or jump straight to Reddit with your filters applied.
        </p>
        <p>
          No more guessing at search syntax. Build your query visually, see exactly what you're
          searching for, and uncover hidden discussions faster.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiSearch /> Search Criteria
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ras-query">Search Query</Label>
                <Input id="ras-query" {...register("query")} placeholder="best marketing tools" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ras-subreddit">Subreddit</Label>
                  <Input id="ras-subreddit" {...register("subreddit")} placeholder="marketing" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ras-author">Author</Label>
                  <Input id="ras-author" {...register("author")} placeholder="username" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ras-site">Site Domain (for link posts)</Label>
                <Input id="ras-site" {...register("site")} placeholder="youtube.com" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiFilter /> Filters & Sort
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="ras-sort">Sort By</Label>
                  <select
                    id="ras-sort"
                    {...register("sort")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="relevance">Relevance</option>
                    <option value="hot">Hot</option>
                    <option value="top">Top</option>
                    <option value="new">New</option>
                    <option value="comments">Most Comments</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <Label htmlFor="ras-time">Time Range</Label>
                  <select
                    id="ras-time"
                    {...register("time")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="all">All Time</option>
                    <option value="year">Past Year</option>
                    <option value="month">Past Month</option>
                    <option value="week">Past Week</option>
                    <option value="day">Past 24 Hours</option>
                    <option value="hour">Past Hour</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-border/50">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="titleOnly"
                    {...register("titleOnly")}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <Label htmlFor="titleOnly">Search Title Only</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="selfText"
                    {...register("selfText")}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <Label htmlFor="selfText">Self Posts Only (Text Only)</Label>
                </div>
              </div>

              <div className="space-y-2 pt-4 border-t border-border/50">
                {/* Group heading, not a <label>: it names the radiogroup below via
                    aria-labelledby, and a <label> with no field of its own is
                    flagged by Chrome. */}
                <span id="ras-nsfw-label" className="text-sm font-medium leading-none">
                  NSFW Content
                </span>
                <div className="flex gap-4" role="radiogroup" aria-labelledby="ras-nsfw-label">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      value="exclude"
                      {...register("nsfw")}
                      className="accent-primary"
                    />{" "}
                    Hide NSFW
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      value="include"
                      {...register("nsfw")}
                      className="accent-primary"
                    />{" "}
                    Include NSFW
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      value="only"
                      {...register("nsfw")}
                      className="accent-primary"
                    />{" "}
                    Only NSFW
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <Card className="bg-muted/50 border-primary/20 shadow-lg">
              <CardHeader>
                <CardTitle>Generated Query</CardTitle>
                <CardDescription>Ready to use on Reddit</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-background border rounded-md p-4 font-mono text-sm break-words min-h-[60px]">
                  {queryPreview || (
                    <span className="text-muted-foreground opacity-50">
                      Start typing to generate query...
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="w-full"
                    disabled={!queryPreview}
                  >
                    <HiClipboardCopy className="mr-2 h-4 w-4" /> Copy Query
                  </Button>
                  <Button
                    onClick={() => window.open(fullUrl, "_blank")}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                    disabled={!queryPreview}
                  >
                    Search on <FaReddit className="mr-2 h-4 w-4" />
                    <HiExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h2 className="font-bold text-lg mb-4">Monitor Reddit for Leads?</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Track keywords, score buying intent (0-100), and manage leads with our{" "}
                <strong>Scout Pro for Reddit</strong> extension.
              </p>
              <Button asChild variant="secondary" className="w-full">
                <Link href="/product/reddit-scout-pro/">View Extension</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ
              items={redditAdvancedSearchFAQ.map((item) => ({
                question: item.question,
                answer: item.answer,
              }))}
              showSupportLink={false}
              className="space-y-4"
            />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
